import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { map, take, catchError, delay, switchMap } from 'rxjs/operators';
import { Subject, of, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UserNavigation } from '../models/user-navigation.model';
import { NavigationItem } from '../models/navigation-item.model';
import { UserNavigationResult } from '../models/user-navigation-result.model';
import { MenuItem } from 'primeng/api';
import { Breadcrumb } from '../models/breadcrumb.model';
import { LoggingMessageService } from './logging-message.service';
import { NavigationMenuTypeEnum } from '../models/navigation-menu-type-enum';
import { UserNavigationMappingService } from './user-navigation-mapping.service';
import { NavigationUtils } from '../navigation-utils';
import { SubscriptionChangeService } from './subscription-change.service';

@Injectable({
  providedIn: 'root'
})

export class UserNavigationService {
  private navigationCompleteSource = new Subject<string>();
  public navigationComplete$ = this.navigationCompleteSource.asObservable();
  private _BASE_URL: string = environment.BusinessApi;
  private _LOCAL_NAVIGATION_API = environment.LocalJsonApi;
  public breadcrumbs = new Array<Breadcrumb>();
  private _displayedMenuType: NavigationMenuTypeEnum;
  private _cachedMenuType: NavigationMenuTypeEnum;
  private _singleHospitalUserNavigation: UserNavigation;
  private _singleHospitalUserNavigationCache: UserNavigation;
  private _facilityNavigationCache: Map<string, UserNavigation> = new Map<string, UserNavigation>();
  private _singleHospitalMenuItemCache: MenuItem[] | MenuItem[][];
  private _singleMenuItems: MenuItem[] | MenuItem[][];
  private _fullUserNavigation: UserNavigation;
  private _fullUserNavigationCache: UserNavigation;
  private _fullMenuItemCache: MenuItem[] | MenuItem[][];
  private _fullMenuItems: MenuItem[] | MenuItem[][];
  private _hospitalLevelUserNavigationCache: UserNavigation;
  private _hospitalLevelUserNavigation: UserNavigation;
  private _hospitalLevelMenuItemCache: MenuItem[] | MenuItem[][];
  private _hospitalLevelMenuItems: MenuItem[] | MenuItem[][];
  private _NO_DATA = { data: [] };

  constructor(private http: HttpClient,
    private logger: LoggingMessageService,
    private navigationMappingService: UserNavigationMappingService,
    private subscriptionService: SubscriptionChangeService) {
    this.CachedMenuType = NavigationMenuTypeEnum.NotSet;
  }

  get DisplayedMenuItems(): MenuItem[] | MenuItem[][] {
    let menuItems: MenuItem[] | MenuItem[][];
    switch (this.DisplayedMenuType) {
      case NavigationMenuTypeEnum.FullMenu:
        menuItems = this._fullMenuItems;
        break;
      case NavigationMenuTypeEnum.SingleHospital:
        menuItems = this._singleMenuItems;
        break;
      case NavigationMenuTypeEnum.HospitalLevel:
        menuItems = this._hospitalLevelMenuItems;
        break;
      default:
        break;
    }
    return menuItems;
  }

  set DisplayedMenuItems(value: MenuItem[] | MenuItem[][]) {
    switch (this.DisplayedMenuType) {
      case NavigationMenuTypeEnum.FullMenu:
        this._fullMenuItems = value;
        break;
      case NavigationMenuTypeEnum.SingleHospital:
        this._singleMenuItems = value;
        break;
      case NavigationMenuTypeEnum.HospitalLevel:
        this._hospitalLevelMenuItems = value;
        break;
      default:
        break;
    }
  }

  get DisplayedNavigationMenu(): UserNavigation {
    let userNavigation: UserNavigation;
    switch (this.DisplayedMenuType) {
      case NavigationMenuTypeEnum.FullMenu:
        userNavigation = this._fullUserNavigation;
        break;
      case NavigationMenuTypeEnum.SingleHospital:
        userNavigation = this._singleHospitalUserNavigation;
        break;
      case NavigationMenuTypeEnum.HospitalLevel:
        userNavigation = this._hospitalLevelUserNavigation;
        break;
      default:
        break;
    }
    return userNavigation;
  }

  set DisplayedNavigationMenu(value: UserNavigation) {
    switch (this.DisplayedMenuType) {
      case NavigationMenuTypeEnum.FullMenu:
        this._fullUserNavigation = value;
        break;
      case NavigationMenuTypeEnum.SingleHospital:
        this._singleHospitalUserNavigation = value;
        break;
      case NavigationMenuTypeEnum.HospitalLevel:
        this._hospitalLevelUserNavigation = value;
        break;
      default:
        break;
    }
  }

  public setNavigationState(menuType: NavigationMenuTypeEnum, menuItems: MenuItem[] | MenuItem[][], navigationMenu: UserNavigation): void {
    this.DisplayedMenuType = menuType;
    this.DisplayedMenuItems = menuItems;
    this.DisplayedNavigationMenu = navigationMenu;
  }

  public updateDisplayedMenuItems(menuItems: MenuItem[] | MenuItem[][]): void {
    this.DisplayedMenuItems = menuItems;
  }

  public updateDisplayedNavigationMenu(navigationMenu: UserNavigation): void {
    this.DisplayedNavigationMenu = navigationMenu;
  }

  get CachedNavigationMenu(): UserNavigation {
    let userNavigation: UserNavigation;
    switch (this.CachedMenuType) {
      case NavigationMenuTypeEnum.FullMenu:
        userNavigation = this.FullUserNavigationCache;
        break;
      case NavigationMenuTypeEnum.SingleHospital:
        userNavigation = this.SingleHospitalUserNavigationCache;
        break;
      case NavigationMenuTypeEnum.HospitalLevel:
        userNavigation = this.HospitalLevelUserNavigationCache;
        break;
      default:
        break;
    }
    return userNavigation;
  }

  get CachedMenuItems(): MenuItem[] | MenuItem[][] {
    let menuItems: MenuItem[] | MenuItem[][];
    switch (this.CachedMenuType) {
      case NavigationMenuTypeEnum.FullMenu:
        menuItems = this.FullMenuItemCache;
        break;
      case NavigationMenuTypeEnum.SingleHospital:
        menuItems = this.SingleHospitalMenuItemCache;
        break;
      case NavigationMenuTypeEnum.HospitalLevel:
        menuItems = this.HospitalLevelMenuItemCache;
        break;
      default:
        break;
    }
    return menuItems;
  }

  get CachedMenuType(): NavigationMenuTypeEnum {
    return this._cachedMenuType;
  }

  set CachedMenuType(value: NavigationMenuTypeEnum) {
    this._cachedMenuType = value;
  }

  get FullUserNavigationCache(): UserNavigation {
    return this._fullUserNavigationCache;
  }

  set FullUserNavigationCache(value: UserNavigation) {
    this._fullUserNavigationCache = value;
    this.CachedMenuType = NavigationMenuTypeEnum.FullMenu;
    this.clearHospitalLevelCache();
    this.clearSingleHospitalCache();
  }

  get FullMenuItemCache(): MenuItem[] | MenuItem[][] {
    return this._fullMenuItemCache;
  }

  set FullMenuItemCache(value: MenuItem[] | MenuItem[][]) {
    this._fullMenuItemCache = value;
  }

  get SingleHospitalUserNavigationCache(): UserNavigation {
    return this._singleHospitalUserNavigationCache;
  }

  set SingleHospitalUserNavigationCache(value: UserNavigation) {
    this._singleHospitalUserNavigationCache = value;
    if (this.CachedMenuType !== NavigationMenuTypeEnum.FullMenu) {
      this.CachedMenuType = NavigationMenuTypeEnum.SingleHospital;
    }
    this.clearHospitalLevelCache();
  }

  get SingleHospitalMenuItemCache(): MenuItem[] | MenuItem[][] {
    return this._singleHospitalMenuItemCache;
  }

  set SingleHospitalMenuItemCache(value: MenuItem[] | MenuItem[][]) {
    this._singleHospitalMenuItemCache = value;
  }

  get HospitalLevelMenuItemCache(): MenuItem[] | MenuItem[][] {
    return this._hospitalLevelMenuItemCache;
  }

  set HospitalLevelMenuItemCache(value: MenuItem[] | MenuItem[][]) {
    this._hospitalLevelMenuItemCache = value;
  }

  get HospitalLevelUserNavigationCache(): UserNavigation {
    return this._hospitalLevelUserNavigationCache;
  }

  set HospitalLevelUserNavigationCache(value: UserNavigation) {
    this._hospitalLevelUserNavigationCache = value;
    if (this.CachedMenuType === NavigationMenuTypeEnum.NotSet) {
      this.CachedMenuType = NavigationMenuTypeEnum.HospitalLevel;
    }
  }

  get DisplayedMenuType(): NavigationMenuTypeEnum {
    return this._displayedMenuType;
  }

  set DisplayedMenuType(value: NavigationMenuTypeEnum) {
    this._displayedMenuType = value;
  }

  // Full Menu
  get UserNavigationFull(): UserNavigation {
    return this._fullUserNavigation;
  }

  set UserNavigationFull(value: UserNavigation) {
    this._fullUserNavigation = value;
  }

  get MenuItemsFull(): MenuItem[] | MenuItem[][] {
    return this._fullMenuItems;
  }

  set MenuItemsFull(value: MenuItem[] | MenuItem[][]) {
    this._fullMenuItems = value;
  }

  // Single Hospital Menu
  get UserNavigationSingleHospital(): UserNavigation {
    return this._singleHospitalUserNavigation;
  }

  set UserNavigationSingleHospital(value: UserNavigation) {
    this._singleHospitalUserNavigation = value;
  }

  get MenuItemsSingleHospital(): MenuItem[] | MenuItem[][] {
    return this._singleMenuItems;
  }

  set MenuItemsSingleHospital(value: MenuItem[] | MenuItem[][]) {
    this._singleMenuItems = value;
  }

  // Hospital Level Menu
  get UserNavigationHospitalLevel(): UserNavigation {
    return this._hospitalLevelUserNavigation;
  }

  set UserNavigationHospitalLevel(value: UserNavigation) {
    this._hospitalLevelUserNavigation = value;
  }

  get MenuItemsHospitalLevel(): MenuItem[] | MenuItem[][] {
    return this._hospitalLevelMenuItems;
  }

  set MenuItemsHospitalLevel(value: MenuItem[] | MenuItem[][]) {
    this._hospitalLevelMenuItems = value;
  }

  private _selectedHospitalMenuItem: MenuItem;
  get SelectedHospitalMenuItem(): MenuItem {
    return this._selectedHospitalMenuItem;
  }

  set SelectedHospitalMenuItem(value: MenuItem) {
    this._selectedHospitalMenuItem = value;
  }

  private _selectedUnitMenuItem: MenuItem;
  get SelectedUnitMenuItem(): MenuItem {
    return this._selectedUnitMenuItem;
  }

  set SelectedUnitMenuItem(value: MenuItem) {
    this._selectedUnitMenuItem = value;
  }

  private _hospitalMenuItemCache: Map<string, Array<MenuItem>> = new Map<string, Array<MenuItem>>();;
  get HospitalMenuItemCache(): Map<string, Array<MenuItem>> {
    return this._hospitalMenuItemCache;
  }

  set HospitalMenuItemCache(value: Map<string, Array<MenuItem>>) {
    this._hospitalMenuItemCache = value;
  }

  private _unitMenuItemCache: Map<string, Array<MenuItem>> = new Map<string, Array<MenuItem>>();;
  get UnitMenuItemCache(): Map<string, Array<MenuItem>> {
    return this._unitMenuItemCache;
  }

  set UnitMenuItemCache(value: Map<string, Array<MenuItem>>) {
    this._unitMenuItemCache = value;
  }

  public HospitalItemCacheExists(subid: string): boolean {
    const stubbedMenuItems = this.HospitalMenuItemCache.get(subid);
    const exists = ((stubbedMenuItems) && (stubbedMenuItems.length > 0));
    return exists;
  }

  public UnitItemCacheExists(hospid: string): boolean {
    const stubbedMenuItems = this.UnitMenuItemCache.get(hospid);
    const exists = ((stubbedMenuItems) && (stubbedMenuItems.length > 0));
    return exists;
  }

  // Clone
  public CloneUserNavigation(userNav: UserNavigation): UserNavigation {
    let clonedUserNav: UserNavigation;
    if (userNav) {
      const nav: any = userNav as any;
      clonedUserNav = this.navigationMappingService.cloneUserNavigation(nav);
    }
    return clonedUserNav;
  }

  public clearHospitalLevelCache() {
    this.HospitalLevelMenuItemCache = null;
    this.HospitalLevelUserNavigationCache = null;
  }

  public clearSingleHospitalCache() {
    this.SingleHospitalMenuItemCache = null;
    this.SingleHospitalUserNavigationCache = null;
  }

  private clearFullCache() {
    this.FullUserNavigationCache = null;
    this.FullMenuItemCache = null;
  }

  public clearMenus() {
    this._selectedHospitalMenuItem = null;
    this._selectedUnitMenuItem = null;
    this._hospitalLevelUserNavigation = null;
    this._hospitalLevelMenuItems = null;
    this.clearFullCache();
    this.clearSingleHospitalCache();
    this._facilityNavigationCache.clear();
    this.CachedMenuType = NavigationMenuTypeEnum.NotSet;
    this.DisplayedMenuType = NavigationMenuTypeEnum.NotSet;
  }

  public emitNavigationComplete(linkid: string) {
    this.navigationCompleteSource.next(linkid);
  }

  public getFullUserNavigationCompressed(): Observable<UserNavigationResult> {
    if (environment.UseServerForUserNavigation) {
      return this.getFullUserNavigationCachedAndDeCompressedFromBusinessApi();
    } else {
      return this.getFullUserNavigationDeCompressedFromLocalApi();
    }
  }

  public getUserNavigationWithFallback(): Observable<UserNavigationResult> {
    if (environment.UseLocalCacheNavStub) {
      return this.getFullUserNavigationDeCompressedFromLocalApi();
    }
    const cached$ = environment.UseServerForUserNavigation ? this.getFullUserNavigationCachedAndDeCompressedFromBusinessApi() : this.getFullUserNavigationDeCompressedFromLocalApi();
    const fallback$ = environment.UseServerForUserNavigation ? this.getHospitalListUserNavigationFromBusinessApi() : this.getHospitalListUserNavigationFromLocalApi();
    return cached$.pipe(
      switchMap(r => (r && r.Ok && r.Status === 200) ? of(r) : fallback$),
      take(1)
    );
  }

  public getSingleHospitalUserNavigation(hospid: string): Observable<UserNavigationResult> {
    // 1. Return from facility cache if available
    const cached = this._facilityNavigationCache.get(hospid);
    if (cached) {
      const result = new UserNavigationResult(true, 200, 'OK (from cache)', cached as any, NavigationMenuTypeEnum.SingleHospital);
      return of(result).pipe(take(1));
    }
    // 2. Fall back to HTTP call
    const call$ = environment.UseServerForUserNavigation
      ? this.getSingleHospitalUserNavigationFromBusinessApi(hospid)
      : this.getSingleHospitalUserNavigationFromLocalApi();

    // 3. Store successful result in cache for future use
    return call$.pipe(
      map(r => {
        if (r && r.Ok && r.Status === 200) {
          this._facilityNavigationCache.set(hospid, r.UserNavigation as any);
        }
        return r;
      }),
      take(1)
    );
  }

  public getHospitalListUserNavigation(): Observable<UserNavigationResult> {
    if (environment.UseServerForUserNavigation) {
      return this.getHospitalListUserNavigationFromBusinessApi();
    } else {
      return this.getHospitalListUserNavigationFromLocalApi();
    }
  }

  public userHasAccessToLink(permItemId: string): boolean {
    let userHasAccess = false;
    if (this._fullUserNavigation && (this._fullUserNavigation.Items.length > 0)) {
      const fullMenu = this.FullMenuItemCache;
      const foundItem = this.getMenuItem(permItemId, fullMenu);
      userHasAccess = (foundItem) ? true : false;
    } else if (this._singleHospitalUserNavigation && (this._singleHospitalUserNavigation.Items.length > 0)) {
      const singleMenu = this.SingleHospitalMenuItemCache;
      const foundItem = this.getMenuItem(permItemId, singleMenu);
      userHasAccess = (foundItem) ? true : false;
    }
    return userHasAccess;
  }

  public getMenuItemFromCache(menuItemId: string): MenuItem {
    let mi: MenuItem;
    const isHospitalLink = NavigationUtils.IsLastPartHospitalItemFromPermId(menuItemId);
    const isUnitLink = NavigationUtils.IsLastPartUnitItemFromPermId(menuItemId);
    if (isHospitalLink) {
      const subid = NavigationUtils.GetSubscriptionIdFromPermId(menuItemId);
      const hospMenuItems = this.HospitalMenuItemCache.get(subid);
      if (hospMenuItems) {
        mi = hospMenuItems.find(hmi => hmi.id === menuItemId);
      }
    } else if (isUnitLink) {
      const hospparts = menuItemId.split("_u");
      const unitMenuItems = this.UnitMenuItemCache.get(hospparts[0]);
      mi = unitMenuItems.find(umi => umi.id === menuItemId);
    }
    return mi;
  }

  public getMenuItem(menuItemId: string, items: any[]): MenuItem {
    const mi = this.getSubMenuItem(menuItemId, items);
    return mi;
  }

  private getSubMenuItem(menuItemId: string, subMenuItems: Array<any>): MenuItem {
    let mi: MenuItem;
    if (subMenuItems) {
      // tslint:disable-next-line:prefer-for-of
      for (let index = 0; index < subMenuItems.length; index++) {
        const item = subMenuItems[index];
        if (item.id === menuItemId) {
          mi = item;
          break;
        }
        if (!mi && item.items) {
          mi = this.getSubMenuItem(menuItemId, item.items);
        }
      }
    }
    return mi;
  }

  private getFullUserNavigationCachedAndDeCompressedFromBusinessApi(): Observable<UserNavigationResult> {
    const webmethod = 'CacheNav';
    const media = environment.GetCacheNav;
    const uri = `${this._BASE_URL}${webmethod}`;

    return this.http.get<any>(
      uri,
      {
        headers: { Accept: media },
        observe: 'response'
      }
    )
      .pipe(
        map(response => {
          this.logger.debug('user-navigation', 'getFullUserNavigationCachedAndDeCompressedFromBusinessApi', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString,  'Received menu items', '');
          let result: UserNavigationResult;
          if (response instanceof HttpResponse) {
            this.logger.debug('user-navigation', 'getFullUserNavigationCachedAndDeCompressedFromBusinessApi', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString,   `Response Status:${response.status}`, '');
            if (response.status === 200) {
              result = new UserNavigationResult(response.ok, response.status, response.statusText, response.body, NavigationMenuTypeEnum.FullMenu);
            } else if (response.status === 204) {
              result = new UserNavigationResult(response.ok, response.status, response.statusText, {}, NavigationMenuTypeEnum.FullMenu);
            }
          }
          if (response instanceof HttpErrorResponse) {
            result = new UserNavigationResult(response.ok, response.status, response.statusText, this._NO_DATA, NavigationMenuTypeEnum.FullMenu);
          }

          return result;
        }),
        catchError(error => {
          return this.handleGetNavigationError(`getFullUserNavigationCachedAndDeCompressedFromBusinessApi failed`, error, NavigationMenuTypeEnum.FullMenu);
        }),
        take(1)
      );
  }

  private getFullUserNavigationDeCompressedFromLocalApi(): Observable<UserNavigationResult> {
    const uri = `${this._LOCAL_NAVIGATION_API}user-navigation-full-sub.json`;

    return this.http.get<any>(
      uri,
      {
        headers: { 'Accept-Encoding': 'gzip' },
        observe: 'response'
      }
    )
      .pipe(
        map(response => {
          delay(200000);
          let result: UserNavigationResult;
          if (response instanceof HttpResponse && response.status === 200) {
              result = new UserNavigationResult(response.ok, response.status, response.statusText, response.body, NavigationMenuTypeEnum.FullMenu);
          } else if (response instanceof HttpErrorResponse) {
            result = new UserNavigationResult(response.ok, response.status, response.statusText, this._NO_DATA, NavigationMenuTypeEnum.FullMenu);
          }
          return result;
        }),
        catchError(error => {
          return this.handleGetNavigationError(`getFullUserNavigationDeCompressedFromLocalApi failed`, error, NavigationMenuTypeEnum.FullMenu);
        }),
        take(1)
      );
  }

  // Single Hospital Menu Service Calls
  private getSingleHospitalUserNavigationFromLocalApi(): Observable<UserNavigationResult> {
    const uri = `${this._LOCAL_NAVIGATION_API}user-navigation-single-hospital-multi-sub.json`;

    return this.http.get<any>(
      uri, { observe: 'response' }
    )
      .pipe(
        map(response => {
          let result: UserNavigationResult;
          if (response instanceof HttpResponse && response.status === 200) {
              result = new UserNavigationResult(response.ok, response.status, response.statusText, response.body, NavigationMenuTypeEnum.SingleHospital);
          } else if (response instanceof HttpErrorResponse) {
            result = new UserNavigationResult(response.ok, response.status, response.statusText, this._NO_DATA, NavigationMenuTypeEnum.SingleHospital);
          }
          return result;
        }),
        catchError(error => {
          return this.handleGetNavigationError(`getSingleHospitalUserNavigationFromLocalApi failed`, error, NavigationMenuTypeEnum.SingleHospital);
        }),
        take(1)
      );
  }

  private getSingleHospitalUserNavigationFromBusinessApi(hospid: string): Observable<UserNavigationResult> {
    const webmethod = 'MinNonCacheNav';
    const media = environment.MinNonCacheNavHospitalItemsMedia;
    const uri = `${this._BASE_URL}${webmethod}?hospID=${hospid}`;

    return this.http.get<any>(
      uri,
      {
        headers: { Accept: media },
        observe: 'response'
      }
    )
      .pipe(
        map(response => {
          this.logger.debug('user-navigation', 'getSingleHospitalUserNavigationFromBusinessApi', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString,  'Received menu items', '');
          let result: UserNavigationResult;
          if (response instanceof HttpResponse) {
            this.logger.debug('user-navigation', 'getSingleHospitalUserNavigationFromBusinessApi', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString,  `Response Status:${response.status}`, '');
            if (response.status === 200) {
              result = new UserNavigationResult(response.ok, response.status, response.statusText, response.body, NavigationMenuTypeEnum.SingleHospital);
              // uncomment below to test with only 3 facilties
              // const filtered = { ...response.body };
              // const facilities = filtered.is[2].is;
              // filtered.is[2].is = facilities.filter((f, i) => i < 3);
              // result = new UserNavigationResult(response.ok, response.status, response.statusText, filtered, NavigationMenuTypeEnum.SingleHospital);
            }
          }
          if (response instanceof HttpErrorResponse) {
            result = new UserNavigationResult(response.ok, response.status, response.statusText, this._NO_DATA, NavigationMenuTypeEnum.SingleHospital);
          }
          return result;
        }),
        catchError(error => {
          return this.handleGetNavigationError(`getSingleHospitalUserNavigationFromBusinessApi failed`, error, NavigationMenuTypeEnum.SingleHospital);
        }),
        take(1)
      );
  }

  // Hospital List Menu Service Calls
  private getHospitalListUserNavigationFromLocalApi(): Observable<UserNavigationResult> {
    const uri = `${this._LOCAL_NAVIGATION_API}user-navigation-hospital-level-multi-sub.json`;

    return this.http.get<any>(
      uri, { observe: 'response' }
    )
      .pipe(
        map(response => {
          let result: UserNavigationResult;
          if (response instanceof HttpResponse && response.status === 200) {
              result = new UserNavigationResult(response.ok, response.status, response.statusText, response.body, NavigationMenuTypeEnum.HospitalLevel);
          } else if (response instanceof HttpErrorResponse) {
            result = new UserNavigationResult(response.ok, response.status, response.statusText, this._NO_DATA, NavigationMenuTypeEnum.HospitalLevel);
          }
          return result;
        }),
        catchError(error => {
          return this.handleGetNavigationError(`getHospitalListUserNavigationFromLocalApi failed`, error, NavigationMenuTypeEnum.HospitalLevel);
        }),
        take(1)
      );
  }

  private getHospitalListUserNavigationFromBusinessApi(): Observable<UserNavigationResult> {
    const webmethod = 'MinNonCacheNav';
    const media = environment.MinNonCacheNavHospitalListMedia;
    const uri = `${this._BASE_URL}${webmethod}`;

    return this.http.get<any>(
      uri,
      {
        headers: { Accept: media },
        observe: 'response'
      }
    )
      .pipe(
        map(response => {
          this.logger.debug('user-navigation', 'getHospitalListUserNavigationFromBusinessApi', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString,   'Received menu items', '');
          let result: UserNavigationResult;
          if (response instanceof HttpResponse) {
            this.logger.debug('user-navigation', 'getHospitalListUserNavigationFromBusinessApi', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString,   `Response Status:${response.status}`, '');
            if (response.status === 200) {
               result = new UserNavigationResult(response.ok, response.status, response.statusText, response.body, NavigationMenuTypeEnum.HospitalLevel);
              // uncomment below to test with only 3 facilties
              // const filtered = { ...response.body };
              // const facilities = filtered.is[2].is;
              // filtered.is[2].is = facilities.filter((f, i) => i < 3);
              // result = new UserNavigationResult(response.ok, response.status, response.statusText, filtered, NavigationMenuTypeEnum.HospitalLevel);
            }
          }
          if (response instanceof HttpErrorResponse) {
            result = new UserNavigationResult(response.ok, response.status, response.statusText, this._NO_DATA, NavigationMenuTypeEnum.HospitalLevel);
          }
          return result;
        }),
        catchError(error => {
          return this.handleGetNavigationError(`getHospitalListUserNavigationFromBusinessApi failed`, error, NavigationMenuTypeEnum.HospitalLevel);
        }),
        take(1)
      );
  }

  public clearStubChildItems(navItem: NavigationItem, displayedItems: MenuItem[]) {
    const isSubscriptionLevel = NavigationUtils.IsLastPartSubscriptionItemFromPermId(navItem.PermItemID);
    const isHospitalLevel = NavigationUtils.IsLastPartHospitalItemFromPermId(navItem.PermItemID);
    if (isSubscriptionLevel) {
      this.clearHospitalStubChildItems(navItem, displayedItems)
    }
    if (isHospitalLevel) {
      this.clearUnitStubChildItems(navItem, displayedItems)
    }
    return displayedItems;
  }

  private clearHospitalStubChildItems(navItem: NavigationItem, displayedItems: MenuItem[]) {
    let subMenuItem = this.getMenuItem(navItem.PermItemID, displayedItems);
    if (subMenuItem && subMenuItem.items && subMenuItem.items.length > 0 && this.SelectedHospitalMenuItem) {
      this.SelectedHospitalMenuItem.items = null;
    }
  }

  private clearUnitStubChildItems(navItem: NavigationItem, displayedItems: MenuItem[]) {
      let hospMenuItem = this.getMenuItem(navItem.PermItemID, displayedItems);
      if ((hospMenuItem === null) || (hospMenuItem === undefined)) {
        const subPermId = NavigationUtils.GetParentPermId(navItem.PermItemID);
        const subMenuItem = this.getMenuItem(subPermId, displayedItems);
        if (subMenuItem && subMenuItem.items && subMenuItem.items.length > 0) {
          hospMenuItem = subMenuItem.items[0];
        }
      }
      if (hospMenuItem && hospMenuItem.items && hospMenuItem.items.length > 0) {
        const unitChild = hospMenuItem.items[0];
        const isUnitStub = NavigationUtils.IsStubFromPermId(unitChild.id);
        if (isUnitStub) {
          unitChild.items = null;
        }
      }
  }

  public loadOnlyRequiredHospitalsChildItemsFromCache(navItem: NavigationItem, truncatedMenuItems: MenuItem[], displayedItems: MenuItem[], subscriptionPermId: string, unitMenuItems: Map<string, Array<MenuItem>>): MenuItem[] {
    const activeHospId = NavigationUtils.GetHospitalIdNavItem(navItem);
    const activeUnitId = NavigationUtils.GetUnitIdNavItem(navItem);
    const isHospLevel = NavigationUtils.IsHospitalLevel(navItem.PermItemID);
    const isUnitLevel = NavigationUtils.IsUnitLevel(navItem.PermItemID);

    for (let sindex = 0; sindex < displayedItems.length; sindex++) {
      const subLevelItem = displayedItems[sindex];
      if (subLevelItem.id === subscriptionPermId) {
        for (let hindex = 0; hindex < subLevelItem.items.length; hindex++) {
          const hospLevelItem = subLevelItem.items[hindex];
          const hospLevelId = NavigationUtils.GetHospitalIdFromPermId(hospLevelItem.id);
          const isHospStub = NavigationUtils.IsHospitalStubFromPermId(hospLevelItem.id);
          if (isHospStub || (hospLevelId === activeHospId)) {
            this.SelectedHospitalMenuItem = hospLevelItem;
            this.SelectedUnitMenuItem = null;
            const displayedSub = displayedItems[sindex] as MenuItem;
            const displayedHosp = displayedSub.items[hindex] as MenuItem;
            const hospIndex = (isHospStub) ? 0 : hindex;
            // Safely resolve the corresponding hospital entry in the truncated menu.
            const hospItemsArray = (truncatedMenuItems && truncatedMenuItems[sindex] && truncatedMenuItems[sindex].items)
              ? truncatedMenuItems[sindex].items
              : [];
            const truncatedHosp = hospItemsArray[hospIndex] ?? hospItemsArray[0];
            if (isHospLevel && truncatedHosp) {
              displayedHosp.items = truncatedHosp.items;
            }
            if (hospLevelItem && hospLevelItem.items) {
              for (let uindex = 0; uindex < hospLevelItem.items.length; uindex++) {
                const unitLevelItem = hospLevelItem.items[uindex];
                const isUnitStub = NavigationUtils.IsUnitStubFromPermId(unitLevelItem.id);
                const unitLevelId = NavigationUtils.GetUnitIdFromPermId(unitLevelItem.id);
                if (isUnitLevel && (isUnitStub || unitLevelId === activeUnitId)) {
                  const displayedUnit = displayedHosp.items[uindex] as MenuItem;
                  const parts = navItem.PermItemID.split('_u');
                  const cachedUnits = unitMenuItems.get(parts[0]);
                  let cachedUnit: MenuItem;
                  let cachedUnitItems = new Array<MenuItem>();
                  if (cachedUnits) {
                    cachedUnit = cachedUnits.find(u => u.id === navItem.PermItemID);
                    cachedUnitItems = (cachedUnit) ? cachedUnit.items : new Array<MenuItem>();
                    this.SelectedUnitMenuItem = (cachedUnit) ? cachedUnit : null;
                  }
                  const oldStubId = displayedUnit.id;
                  const oldParts = oldStubId.split('-');
                  const newStubId = oldParts[1] ? `${navItem.PermItemID}-${oldParts[1]}` : navItem.PermItemID;
                  displayedUnit.items = cachedUnitItems;
                  displayedUnit.id = newStubId;
                }
              }
            }
          }
        }
      }
    }

    return displayedItems;
  }

  private handleGetNavigationError(operation: string, error: any, menuType: NavigationMenuTypeEnum) {
    this.logger.error('user-navigation', 'handleGetNavigationError', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString,  `Operation:${operation}`, `Error:${error}`);
    const result = new UserNavigationResult(false, 0, error.message, this._NO_DATA, menuType);
    return of(result);
  }

}
